<template>
  <PageHeader
    breadcrumb
    :items="[
      { label: $t('ui.entities.report.label', 2), to: '/reports' },
      { label: 'Engagement' },
      { label: 'Productivity' }
    ]"
  >
    <template #right>
      <div class="flex items-center gap-2">
        <Select
          v-model="callCenterSelected"
          :options="callCentersAllowed"
          icon="headset"
          value-key="id"
          label-key="name"
          :placeholder="`Select a Call Center`"
          class="min-w-56"
          :readonly="callCentersAllowed.length == 1"
          @select="updateCallCenterSelected"
        ></Select>
        <DatePicker
          v-model="selectedDate"
          mode="single"
          display-as="button"
          display-format="full"
          :max-date="new Date()"
        />
        <div>
          <Button
            label="Update"
            :is-loading="isLoading"
            @click="fetchData"
          />
        </div>
      </div>
    </template>
  </PageHeader>

  <Card>
    <DataTable
      ref="dt"
      v-model:filters="filters"
      export-filename="Shifts & Productivity"
      :value="computedData"
      removable-sort
      sort-field="timestamp"
      :sort-order="-1"
      data-key="id"
      column-resize-mode="fit"
      :paginator="true"
      responsive-layout="scroll"
      filter-display="menu"
      :global-filter-fields="['dealershipId', 'dealershipName']"
      :rows="25"
      :rows-per-page-options="[10, 25, 50]"
      state-key="dt-report-shifts-productivity"
      state-storage="session"
      current-page-report-template="Showing {startRecord}-{endRecord} out of {totalRecords}"
      :loading="isLoading"
    >
      <template #empty>
        <Empty
          v-if="!isLoading"
          :title="callCenterSelected ? 'No data' : `Select a ${$t('ui.entities.departments.callCenter')}`"
        />
      </template>

      <template #header>
        <div class="flex justify-between">
          <span class="flex justify-between gap-4">
            <span>
              <Input
                v-model="filters['global'].value"
                :placeholder="$t('ui.common.search', 1)"
                icon="search"
              />
            </span>
            <Button
              :label="$t('ui.actions.clear', 1)"
              severity="secondary"
              type="button"
              outlined
              icon="filter-slash"
              @click="clearFilters()"
            />
          </span>
        </div>
        <div class="flex justify-between mt-4">
          <span>
            <Button
              label="CSV"
              type="button"
              severity="secondary"
              outlined
              icon="file-excel"
              @click="exportCSV($event)"
            />
          </span>
        </div>
      </template>
      <ColumnGroup type="header">
        <Row>
          <Column :colspan="3" />

          <Column
            header="Presence"
            :colspan="4"
          />
          <Column
            header="Agent status"
            :colspan="4"
          />
        </Row>

        <Row>
          <Column
            field="userId"
            header="User"
            :sortable="true"
          />
          <Column
            field="totalShiftHours"
            header="Scheduled"
            :sortable="true"
          />
          <Column
            field="productiveHours"
            header="Productive"
            :sortable="true"
          />
          <Column
            field="hoursOnline"
            header="Online"
            :sortable="true"
          />
          <Column
            field="hoursIdle"
            header="Idle"
            :sortable="true"
          />
          <Column
            field="hoursOffline"
            header="Offline"
            :sortable="true"
          />
          <Column
            field="hoursInCall"
            header="In Call"
            :sortable="true"
          />
          <Column
            field="hoursAvailable"
            :sortable="true"
          >
            <template #header>
              Available
              <Icon
                v-tippy="{ content: 'Colour reflects time compared to productive' }"
                type="info-circle"
                class="m-2"
              />
            </template>
          </Column>
          <Column
            field="hoursUnavailable"
            header="Unavailable"
            :sortable="true"
          />
          <Column
            field="hoursOnBreak"
            header="On Break"
            :sortable="true"
          />
        </Row>
      </ColumnGroup>
      <Column
        field="userId"
        header="User"
        :sortable="true"
      >
        <template #body="{ data }">
          <div class="flex gap-3 items-center">
            <Avatar
              :url="data.profilePicUrl"
              :text="data.initials"
              size="sm"
            />
            {{ data.fullName || data.id }}
          </div>
        </template>
      </Column>

      <Column
        field="totalShiftHours"
        header="Scheduled"
        :sortable="true"
      >
        <template #body="{ data }">
          {{ data.totalShiftHours }}
        </template>
      </Column>

      <Column
        field="productiveHours"
        header="Productive"
        :sortable="true"
      >
        <template #body="{ data }">
          {{ data.productiveHours }}
        </template>
      </Column>

      <Column
        field="hoursOnline"
        header="Online"
        :sortable="true"
      >
        <template #body="{ data }">
          {{ data.hoursOnline ? formatNumber(data.hoursOnline / 3600000, 'normal', 1) : '' }}
        </template>
      </Column>

      <Column
        field="hoursIdle"
        header="Idle"
        :sortable="true"
      >
        <template #body="{ data }">
          {{ data.hoursIdle ? formatNumber(data.hoursIdle / 3600000, 'normal', 1) : '' }}
        </template>
      </Column>

      <Column
        field="hoursOffline"
        header="Offline"
        :sortable="true"
      >
        <template #body="{ data }">
          {{ data.hoursOffline ? formatNumber(data.hoursOffline / 3600000, 'normal', 1) : '' }}
        </template>
      </Column>

      <Column
        field="hoursInCall"
        header="In Call"
        :sortable="true"
      >
        <template #body="{ data }">
          {{ data.hoursInCall ? formatNumber(data.hoursInCall / 3600000, 'normal', 1) : '' }}
        </template>
      </Column>

      <Column
        field="hoursAvailable"
        header="Available"
        :sortable="true"
      >
        <template #body="{ data }">
          <div
            :class="'text-' + getRAG(formatNumber(data.hoursAvailable / 3600000, 'normal', 1), data.totalShiftHours)"
          >
            {{ data.hoursAvailable ? formatNumber(data.hoursAvailable / 3600000, 'normal', 1) : '' }}
          </div>
        </template>
      </Column>

      <Column
        field="hoursUnavailable"
        header="Unavailable"
        :sortable="true"
      >
        <template #body="{ data }">
          {{ data.hoursUnavailable ? formatNumber(data.hoursUnavailable / 3600000, 'normal', 1) : '' }}
        </template>
      </Column>

      <Column
        field="hoursOnBreak"
        header="On Break"
        :sortable="true"
      >
        <template #body="{ data }">
          {{ data.hoursOnBreak ? formatNumber(data.hoursOnBreak / 3600000, 'normal', 1) : '' }}
        </template>
      </Column>
    </DataTable>
  </Card>
</template>

<script setup>
import { ref, computed } from 'vue'

import Card from '@/components/card/Card.vue'
import PageHeader from '@/components/page/PageHeader.vue'
import DatePicker from '@/components/date-picker/DatePicker.vue'
import Input from '@/components/forms/Input.vue'
import Button from '@/components/button/Button.vue'
import Empty from '@/components/empty/Empty.vue'
import Select from '@/components/forms/Select.vue'
import Avatar from '@/components/avatar/Avatar.vue'
import Icon from '@/components/icon/Icon.vue'

import useApiRequest from '@/composables/useApiRequest'
// stores
import { useAuthStore } from '@/stores/AuthStore.ts'
import { useCallCenterStore } from '@/stores/CallCenterStore.ts'
import { alertToast } from '@/utilities/notification'
import { formatNumber } from '@/utilities/number'

import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import ColumnGroup from 'primevue/columngroup'
import Row from 'primevue/row'
import { FilterMatchMode } from '@primevue/core/api'
import dayjs from 'dayjs'

const dt = ref()
const authStore = useAuthStore()
const callCenterStore = useCallCenterStore()
const selectedDate = ref(dayjs().format('YYYY-MM-DD'))

const agentHoursRequest = useApiRequest()
const agentStatusRequest = useApiRequest()
const usersRequest = useApiRequest()
const users = ref([])
const scheduleData = ref([])
const userActivityData = ref([])
const filters = ref([])
const isLoading = ref(false)

function initFilters() {
  filters.value = {
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    dealershipId: { value: null, matchMode: FilterMatchMode.IN }
  }
}
initFilters()

function clearFilters() {
  initFilters()
}
const callCentersAllowed = callCenterStore.callCenters
const callCenterSelected = ref(null)
if (!localStorage.getItem('report-filter-call-center-selected')) {
  callCenterSelected.value = callCentersAllowed[0].id
} else {
  callCenterSelected.value = parseInt(localStorage.getItem('report-filter-call-center-selected'))
}

function updateCallCenterSelected() {
  localStorage.setItem('report-filter-call-center-selected', callCenterSelected.value)
}

const greenLimit = 5
const amberLimit = 10

const getRAG = (presence, shiftHours) => {
  if (!shiftHours) {
    return ''
  }
  const percentageDifference = ((shiftHours - presence) / shiftHours) * 100
  return percentageDifference <= greenLimit ? 'success' : percentageDifference <= amberLimit ? 'warning' : 'error'
}

function fetchData() {
  if (!selectedDate.value || !callCenterSelected.value) {
    return
  }
  isLoading.value = true

  try {
    usersRequest
      .send({
        endpoint: '/v1/users',
        params: {
          userType: authStore.details.userType == 1 ? 'all' : 'externalUsers',
          callCenterId: callCenterSelected.value,
          showDisabled: false
        }
      })
      .then(response => {
        if (response.success) {
          users.value = response.data
          fetchScheduleData()
        } else {
          alertToast('Failed to fetch users', response.message, 'error')
        }
      })
  } catch (err) {
    alertToast('Failed to fetch users', err.message, 'error')
  }
}

function fetchScheduleData() {
  try {
    agentHoursRequest
      .send({
        method: 'GET',
        endpoint: '/v1/users/schedule/current',
        params: {
          start: dayjs(selectedDate.value).format('YYYY-MM-DD'),
          end: dayjs(selectedDate.value).format('YYYY-MM-DD'),
          callCenterId: callCenterSelected.value
        }
      })
      .then(response => {
        if (response.success) {
          scheduleData.value = response.data
          if (scheduleData.value.length) {
            fetchActivityData()
          } else {
            isLoading.value = false
          }
        } else {
          alertToast('Failed to fetch data', response.message, 'error')
        }
      })
  } catch (err) {
    alertToast('Failed to fetch data', err.message, 'error')
  }
}

function fetchActivityData() {
  try {
    agentStatusRequest
      .send({
        endpoint: '/v1/users/activity/timelines',
        params: {
          callCenterId: callCenterSelected.value,
          date: dayjs(selectedDate.value).format('YYYY-MM-DD')
        }
      })
      .then(response => {
        if (response.success) {
          userActivityData.value = []
          response.data.forEach(agent => {
            const hoursOnlineSummarized = summarizeActivity(agent.presenceLogs, 'online')
            const hoursOfflineSummarized = summarizeActivity(agent.presenceLogs, 'offline')
            const hoursIdleSummarized = summarizeActivity(agent.presenceLogs, 'idle')
            const hoursInCallSummarized = summarizeActivity(agent.callAgentStatuses, 'on-call')
            const hoursAvailableSummarized = summarizeActivity(agent.callAgentStatuses, 'available')
            const hoursUnavailableSummarized = summarizeActivity(agent.callAgentStatuses, 'unavailable')
            const hoursOnBreakSummarized = summarizeActivity(agent.callAgentStatuses, 'break')

            userActivityData.value.push({
              ...agent,
              hoursOnline: hoursOnlineSummarized,
              hoursOffline: hoursOfflineSummarized,
              hoursIdle: hoursIdleSummarized,
              hoursInCall: hoursInCallSummarized,
              hoursAvailable: hoursAvailableSummarized,
              hoursUnavailable: hoursUnavailableSummarized,
              hoursOnBreak: hoursOnBreakSummarized
            })
          })
        } else {
          alertToast('Failed to fetch data', response.message, 'error')
        }
        isLoading.value = false
      })
  } catch (err) {
    alertToast('Failed to fetch data', err.message, 'error')
  }
}

fetchData()

function summarizeActivity(periods, status) {
  if (periods.length > 0) {
    periods[periods.length - 1].end = periods[periods.length - 1].start
  }

  const periodsFiltered = periods.filter(period => period.status == status)

  periodsFiltered.forEach(period => {
    period.duration = new Date(period.end).getTime() - new Date(period.start).getTime()
  })

  const totalDuration = periodsFiltered.reduce((acc, period) => acc + period.duration, 0)

  return totalDuration
}

const exportCSV = () => {
  dt.value.exportCSV()
}

const computedData = computed(() => {
  const result = {}

  if (!users.value.length || !scheduleData.value.length || !userActivityData.value.length) {
    return []
  }

  users.value.forEach(user => {
    const userSchedule = scheduleData.value.find(agent => agent.userId == user.id)
    const userActivity = userActivityData.value.find(agent => agent.id == user.id)

    result[user.id] = {
      id: user.id,
      fullName: user.fullName,
      profilePicUrl: user.profilePicUrl,
      initials: user.initials,
      productiveHours: userSchedule?.productiveHours ?? null,
      totalShiftHours: userSchedule?.totalShiftHours ?? null,
      hoursOnline: userActivity?.hoursOnline ?? null,
      hoursOffline: userActivity?.hoursOffline ?? null,
      hoursIdle: userActivity?.hoursIdle ?? null,
      hoursInCall: userActivity?.hoursInCall ?? null,
      hoursAvailable: userActivity?.hoursAvailable ?? null,
      hoursUnavailable: userActivity?.hoursUnavailable ?? null,
      hoursOnBreak: userActivity?.hoursOnBreak ?? null
    }
  })

  return Object.values(result)
})
</script>

<style></style>
